"use client"
import {
  FieldError,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { addressSchema, AddressFormData } from "./schema"
import { LabeledInput } from "@/components/cells"
import { Button } from "@/components/atoms"
import { addCustomerAddress, updateCustomerAddress } from "@/lib/data/customer"
import { HttpTypes } from "@medusajs/types"
import CountrySelect from "@/components/cells/CountrySelect/CountrySelect"
import ProvinceSelect from "@/components/molecules/ProvinceSelect/ProvinceSelect"
import { useState } from "react"

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
] as const;
import { useTranslate } from "@tolgee/react"

interface Props {
  defaultValues?: AddressFormData
  regions: HttpTypes.StoreRegion[]
  handleClose?: () => void
}

export const emptyDefaultAddressValues = {
  addressName: "",
  firstName: "",
  lastName: "",
  address: "",
  city: "",
  countryCode: "",
  postalCode: "",
  company: "",
  province: "",
  phone: "",
  metadata: {},
  isDefaultShipping: false,
}

export const AddressForm: React.FC<Props> = ({ defaultValues, ...props }) => {
  const methods = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: defaultValues || emptyDefaultAddressValues,
  })

  return (
    <FormProvider {...methods}>
      <Form {...props} />
    </FormProvider>
  )
}

const Form: React.FC<Props> = ({ regions, handleClose }) => {
  const { t } = useTranslate()
  const [error, setError] = useState<string>()
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useFormContext()

  const region = {
    countries: regions.flatMap((region) => region.countries),
  }

  const submit = async (data: FieldValues) => {
    const formData = new FormData()
    formData.append("addressId", data.addressId || "")
    formData.append("address_name", data.addressName)
    formData.append("first_name", data.firstName)
    formData.append("last_name", data.lastName)
    formData.append("address_1", data.address)
    formData.append("address_2", "")
    formData.append("province", data.province)
    formData.append("city", data.city)
    formData.append("country_code", data.countryCode)
    formData.append("postal_code", data.postalCode)
    formData.append("company", data.company)
    formData.append("phone", data.phone)
    formData.append("is_default_shipping", data.isDefaultShipping ? "true" : "false")

    const res = data.addressId
      ? await updateCustomerAddress(formData)
      : await addCustomerAddress(formData)

    if (!res.success) {
      setError(res.error)
      return
    }

    setError("")
    handleClose && handleClose()
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="px-4 space-y-4">
        <div className="max-w-full grid grid-cols-2 items-top gap-4 mb-4">
          <LabeledInput
            label={t("address_form.address_name")}
            placeholder={t("address_form.type_address_name")}
            className="col-span-2"
            error={errors.firstName as FieldError}
            {...register("addressName")}
          />
          <LabeledInput
            label={t("address_form.first_name")}
            placeholder={t("address_form.type_first_name")}
            error={errors.firstName as FieldError}
            {...register("firstName")}
          />
          <LabeledInput
            label={t("address_form.last_name")}
            placeholder={t("address_form.type_last_name")}
            error={errors.firstName as FieldError}
            {...register("lastName")}
          />
          <LabeledInput
            label={t("address_form.company_optional")}
            placeholder={t("address_form.type_company")}
            error={errors.company as FieldError}
            {...register("company")}
          />
          <LabeledInput
            label={t("address_form.address")}
            placeholder={t("address_form.type_address")}
            error={errors.address as FieldError}
            {...register("address")}
          />
          <LabeledInput
            label={t("address_form.city")}
            placeholder={t("address_form.type_city")}
            error={errors.city as FieldError}
            {...register("city")}
          />
          <LabeledInput
            label={t("address_form.postal_code")}
            placeholder={t("address_form.type_postal_code")}
            error={errors.postalCode as FieldError}
            {...register("postalCode")}
          />
          {watch("countryCode") === "us" ? (
            <div>
              <ProvinceSelect
                label={t("address_form.state_province")}
                {...register("province")}
                value={watch("province")}
                provinces={US_STATES}
                errors={errors.province ? {province: errors.province as FieldError} : undefined}
              />
              {errors.province && (
                <p className="label-sm text-negative">
                  {(errors.province as FieldError).message}
                </p>
              )}
            </div>
          ) : (
            <LabeledInput
              label={t("address_form.state_province")}
              placeholder={t("address_form.type_state_province")}
              error={errors.province as FieldError}
              {...register("province")}
            />
          )}
          <div>
            <CountrySelect
              region={region as HttpTypes.StoreRegion}
              {...register("countryCode")}
              value={watch("countryCode")}
              className="h-12"
            />
            {errors.countryCode && (
              <p className="label-sm text-negative">
                {(errors.countryCode as FieldError).message}
              </p>
            )}
          </div>

          <LabeledInput
            label={t("address_form.phone")}
            placeholder={t("address_form.type_phone_number")}
            error={errors.phone as FieldError}
            {...register("phone")}
          />
        </div>
        
        {/* 默认地址复选框 */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isDefaultShipping"
            {...register("isDefaultShipping")}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="isDefaultShipping" className="label-md text-secondary">
            {t("address_form.set_as_default_shipping")}
          </label>
        </div>

        {error && <p className="label-md text-negative">{error}</p>}
        <Button className="w-full ">{t("address_form.save_address")}</Button>
      </div>
    </form>
  )
}
